import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError } from '~/api/errors';
import { ConfirmDialog } from '~/components/common/confirm-dialog';
import { Button } from '~/components/ui/button';
import { Label } from '~/components/ui/label';
import { Textarea } from '~/components/ui/textarea';
import { useShowCostAmounts } from '~/hooks/useCanSeeCost';
import { extractApiError } from '~/utils/extract-api-error';

import { useCostDecision } from '../hooks/use-cost-estimate';
import type { CostApprovalMode, CostEstimate } from '../types/cost-estimate.types';
import { formatUsd } from '../utils/format-usd';

/** What the confirm dialog is for: a rejection, or an approval in one of the two modes. */
type DecisionChoice = 'REJECTED' | CostApprovalMode;

/**
 * Reject / Approve Vertex only / Approve both (Vertex + GPT-5 fallback) buttons, each behind a
 * confirm dialog with optional notes, for one estimate. Shared by the run card, the Cost
 * approvals list and the estimate details page. The buttons show only while the estimate is
 * pending and decidable; the dialog stays mounted regardless, so a host that keeps this
 * mounted (the run card) does not lose an open dialog or the in-flight decision's callbacks
 * when a poll moves the estimate out of pending. `workflowId` is the run id the host reads
 * the estimate by (defaults to the estimate's own).
 */
export function CostDecisionActions({
  estimate,
  workflowId = estimate.workflowId,
  testId = 'cost-decision',
}: {
  estimate: CostEstimate;
  workflowId?: string;
  /** `data-testid` prefix: `-reject-button`, `-approve-vertex-button`, `-approve-fallback-button`, `-dialog`. */
  testId?: string;
}) {
  const { t } = useTranslation('common');
  const decision = useCostDecision(workflowId);
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  // Last choice opened; kept while the dialog animates out so its copy doesn't flip to Reject.
  const [choice, setChoice] = useState<DecisionChoice>('REJECTED');
  const [notes, setNotes] = useState('');
  // This estimate was just decided here. A host showing it from another query (the Cost
  // approvals list) still holds it as pending until its refetch lands — no re-enabled buttons.
  const decidedHere = decision.data?.id === estimate.id;
  const canAct = estimate.status === 'PENDING_APPROVAL' && estimate.canDecide && !decidedHere;
  // Which button is in flight, so only that one spins.
  const pendingChoice: DecisionChoice | null = decision.isPending
    ? decision.variables.decision === 'REJECTED'
      ? 'REJECTED'
      : (decision.variables.mode ?? 'WITH_FALLBACK')
    : null;
  // Cost display switched off (or a customer): the dialogs keep their copy, without the $ totals.
  const showAmounts = useShowCostAmounts(estimate.amountsHidden);
  const projectedTotal = showAmounts && estimate.total ? formatUsd(estimate.total.avgUsd) : null;
  const geminiTotal =
    showAmounts && estimate.scenarios?.geminiOnly
      ? formatUsd(estimate.scenarios.geminiOnly.avgUsd)
      : null;

  /** Opens the confirm dialog for one choice. */
  function ask(next: DecisionChoice) {
    setChoice(next);
    setConfirmOpen(true);
  }

  /** Sends the decision (with the approval mode); 403 / 409 get dedicated copy, and the hook refetches either way. */
  function decide(picked: DecisionChoice) {
    const trimmed = notes.trim();
    const isReject = picked === 'REJECTED';
    decision.mutate(
      {
        decision: isReject ? 'REJECTED' : 'APPROVED',
        ...(isReject ? {} : { mode: picked }),
        ...(trimmed ? { notes: trimmed } : {}),
      },
      {
        onSuccess: (recorded) => {
          // The mode the backend RECORDED, not the button: a backend that ignores `mode`
          // approves with fallback, and the toast must not claim Vertex only.
          toast.success(
            isReject
              ? t('costEstimate.toast.rejected')
              : recorded.approvalMode === 'VERTEX_ONLY'
                ? t('costEstimate.toast.approvedVertexOnly')
                : t('costEstimate.toast.approved'),
          );
          setConfirmOpen(false);
          setNotes('');
        },
        onError: (err) => {
          setConfirmOpen(false);
          if (err instanceof ApiError && err.statusCode === 403) {
            toast.error(t('costEstimate.toast.notApprover'));
          } else if (err instanceof ApiError && err.statusCode === 409) {
            toast.error(t('costEstimate.toast.alreadyDecided'));
          } else {
            toast.error(extractApiError(err, t('costEstimate.toast.decisionFailed')));
          }
        },
      },
    );
  }

  /** Dialog copy per choice — the two approvals spell out the cost / speed trade-off. */
  function dialogCopy(forChoice: DecisionChoice): { title: string; body: string; confirm: string } {
    switch (forChoice) {
      case 'VERTEX_ONLY':
        return {
          title: t('costApprovals.approveVertexDialog.title'),
          body: geminiTotal
            ? t('costApprovals.approveVertexDialog.description', { total: geminiTotal })
            : t('costApprovals.approveVertexDialog.descriptionNoTotal'),
          confirm: t('costEstimate.approveVertexOnly'),
        };
      case 'WITH_FALLBACK':
        return {
          title: t('costApprovals.approveDialog.title'),
          body: projectedTotal
            ? t('costApprovals.approveDialog.description', { total: projectedTotal })
            : t('costApprovals.approveDialog.descriptionNoTotal'),
          confirm: t('costEstimate.approveWithFallback'),
        };
      case 'REJECTED':
        return {
          title: t('costEstimate.rejectDialog.title'),
          body: t('costEstimate.rejectDialog.description'),
          confirm: t('costEstimate.reject'),
        };
    }
  }
  const copy = dialogCopy(choice);

  return (
    <>
      {canAct ? (
        <div data-testid={`${testId}-actions`} className="flex flex-wrap justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            className="cursor-pointer disabled:cursor-not-allowed"
            disabled={decision.isPending}
            isLoading={pendingChoice === 'REJECTED'}
            data-testid={`${testId}-reject-button`}
            onClick={() => {
              ask('REJECTED');
            }}
          >
            {t('costEstimate.reject')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="cursor-pointer disabled:cursor-not-allowed"
            disabled={decision.isPending}
            isLoading={pendingChoice === 'VERTEX_ONLY'}
            data-testid={`${testId}-approve-vertex-button`}
            onClick={() => {
              ask('VERTEX_ONLY');
            }}
          >
            {t('costEstimate.approveVertexOnly')}
          </Button>
          <Button
            size="sm"
            className="cursor-pointer disabled:cursor-not-allowed"
            disabled={decision.isPending}
            isLoading={pendingChoice === 'WITH_FALLBACK'}
            data-testid={`${testId}-approve-fallback-button`}
            onClick={() => {
              ask('WITH_FALLBACK');
            }}
          >
            {t('costEstimate.approveWithFallback')}
          </Button>
        </div>
      ) : null}

      <ConfirmDialog
        open={isConfirmOpen}
        onOpenChange={(open) => {
          setConfirmOpen(open);
          if (!open) setNotes('');
        }}
        title={copy.title}
        description={
          <div className="flex flex-col gap-3 text-left">
            <p>{copy.body}</p>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`cost-decision-notes-${estimate.id}`}>
                {t('costEstimate.rejectDialog.notesLabel')}
              </Label>
              <Textarea
                id={`cost-decision-notes-${estimate.id}`}
                rows={3}
                maxLength={1000}
                data-testid={`${testId}-notes-input`}
                value={notes}
                placeholder={
                  choice === 'REJECTED'
                    ? t('costEstimate.rejectDialog.notesPlaceholder')
                    : undefined
                }
                onChange={(e) => {
                  setNotes(e.target.value);
                }}
              />
            </div>
          </div>
        }
        confirmLabel={copy.confirm}
        cancelLabel={t('cancel')}
        isPending={decision.isPending}
        testId={`${testId}-dialog`}
        onConfirm={() => {
          decide(choice);
        }}
      />
    </>
  );
}
